import { useState } from "react";

/** B.10: Integrations Marketplace — category-first grid with AI recommendations */

interface Integration {
  id: string;
  name: string;
  icon: string;
  description: string;
  category: string;
  connected: boolean;
  recommended: boolean;
  healthScore: number | null;
}

const MARKETPLACE: Integration[] = [
  // Productivity
  { id: "google", name: "Google Workspace", icon: "G", description: "Calendar, Contacts, Gmail, Drive", category: "Productivity", connected: false, recommended: true, healthScore: null },
  { id: "microsoft", name: "Microsoft 365", icon: "M", description: "Outlook, Teams, Calendar, OneDrive", category: "Productivity", connected: false, recommended: true, healthScore: null },
  { id: "zoom", name: "Zoom", icon: "Z", description: "Video meetings, webinar integration", category: "Productivity", connected: false, recommended: false, healthScore: null },
  // Payments
  { id: "stripe", name: "Stripe", icon: "S", description: "Payments, subscriptions, invoicing", category: "Payments", connected: false, recommended: true, healthScore: null },
  { id: "paypal", name: "PayPal", icon: "P", description: "Online payments", category: "Payments", connected: false, recommended: false, healthScore: null },
  // Marketing
  { id: "facebook", name: "Facebook Ads", icon: "f", description: "Lead ads, custom audiences, pixel", category: "Marketing", connected: false, recommended: true, healthScore: null },
  { id: "google-ads", name: "Google Ads", icon: "G", description: "Search ads, display, remarketing", category: "Marketing", connected: false, recommended: true, healthScore: null },
  { id: "tiktok", name: "TikTok Ads", icon: "T", description: "Lead generation, pixel", category: "Marketing", connected: false, recommended: false, healthScore: null },
  // Messaging
  { id: "whatsapp", name: "WhatsApp Business", icon: "W", description: "Business messaging API", category: "Messaging", connected: false, recommended: true, healthScore: null },
  { id: "telegram", name: "Telegram Bot", icon: "✈", description: "Bot messaging + notifications", category: "Messaging", connected: false, recommended: false, healthScore: null },
  { id: "sms", name: "SMS (Twilio)", icon: "💬", description: "SMS messaging + 10DLC", category: "Messaging", connected: false, recommended: false, healthScore: null },
  // Analytics
  { id: "ga4", name: "Google Analytics 4", icon: "A", description: "Website analytics", category: "Analytics", connected: false, recommended: false, healthScore: null },
  // Automation
  { id: "zapier", name: "Zapier", icon: "⚡", description: "Connect 5000+ apps", category: "Automation", connected: false, recommended: false, healthScore: null },
  { id: "make", name: "Make (Integromat)", icon: "M", description: "Visual automation", category: "Automation", connected: false, recommended: false, healthScore: null },
];

export default function MarketplacePage() {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = ["all", ...new Set(MARKETPLACE.map((i) => i.category))];
  const recommended = MARKETPLACE.filter((i) => i.recommended && !i.connected);
  const connected = MARKETPLACE.filter((i) => i.connected);

  const filtered = MARKETPLACE.filter((i) => {
    if (selectedCategory !== "all" && i.category !== selectedCategory) return false;
    if (search && !i.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="max-w-4xl space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Integrations Marketplace</h2>
        <p className="text-sm text-muted-foreground">Connect your favorite tools to MFM Effect</p>
      </div>

      <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Search integrations..."
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />

      {/* AI Recommendations */}
      {recommended.length > 0 && !search && selectedCategory === "all" && (
        <section>
          <h3 className="text-sm font-medium text-muted-foreground uppercase">Recommended for you</h3>
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            {recommended.slice(0, 4).map((i) => (
              <div key={i.id} className="rounded-md border border-primary/30 bg-primary/5 p-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold">{i.icon}</span>
                  <span className="text-sm font-medium text-foreground">{i.name}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{i.description}</p>
                <button className="mt-2 w-full rounded bg-primary px-2 py-1 text-xs text-primary-foreground">Connect</button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Connected */}
      {connected.length > 0 && (
        <section>
          <h3 className="text-sm font-medium text-muted-foreground uppercase">Connected</h3>
          <div className="mt-3 space-y-2">
            {connected.map((i) => (
              <div key={i.id} className="flex items-center justify-between rounded-md border border-border p-3">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold">{i.icon}</span>
                  <div>
                    <p className="text-sm font-medium text-foreground">{i.name}</p>
                    <p className="text-xs text-muted-foreground">{i.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {i.healthScore !== null && (
                    <span className={`text-xs ${i.healthScore > 90 ? "text-[var(--color-success)]" : "text-yellow-600"}`}>
                      {i.healthScore}% health
                    </span>
                  )}
                  <button className="rounded border border-border px-2 py-1 text-xs hover:bg-accent">Settings</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Category filter */}
      <div className="flex gap-2 overflow-x-auto">
        {categories.map((cat) => (
          <button key={cat} onClick={() => setSelectedCategory(cat)}
            className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm capitalize ${
              selectedCategory === cat ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-accent"
            }`}>
            {cat}
          </button>
        ))}
      </div>

      {/* Full marketplace grid */}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((i) => (
          <div key={i.id} className="rounded-md border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted text-lg font-bold">{i.icon}</div>
              <div>
                <p className="text-sm font-medium text-foreground">{i.name}</p>
                <p className="text-xs text-muted-foreground">{i.category}</p>
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{i.description}</p>
            <button className={`mt-3 w-full rounded-md py-1.5 text-sm font-medium ${
              i.connected ? "border border-border text-foreground" : "bg-primary text-primary-foreground hover:bg-primary/90"
            }`}>
              {i.connected ? "Connected" : "Connect"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
